import { CaretDownIcon } from '@phosphor-icons/react';
import { useId, type ReactNode } from 'react';

import styles from './field-mapping-panel.module.scss';

export type FieldMappingPanelProps = {
  /** Whether the panel is reduced to its title bar. **The page owns this**, so collapsing cannot
   *  unmount anything the page holds — which is the whole of "collapsing or expanding alters no
   *  marked Point" (113600). */
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  /** The Co-ordinates section — ticket 09's. A slot, so that ticket adds the panel's contents
   *  without reopening this file. */
  children?: ReactNode;
};

/**
 * The **Field Mapping** panel, docked at the canvas's inline start (Figma `594:532395` expanded,
 * `594:533307` collapsed).
 *
 * A `<section>` labelled by its own title, not an `<h2>`: the page owns the single document heading
 * ("Field Mapping"), and a second heading with the same words would read the screen's name twice in
 * a heading outline while naming a region that is not the page.
 */
export function FieldMappingPanel({
  collapsed,
  onCollapsedChange,
  children,
}: FieldMappingPanelProps) {
  const titleId = useId();

  return (
    <section
      aria-labelledby={titleId}
      className={[styles.panel, collapsed ? '' : styles.expanded].filter(Boolean).join(' ')}
    >
      <div className={styles.titleBar}>
        <p id={titleId} className={styles.title}>
          Field Mapping
        </p>
        <button
          type="button"
          className={styles.chevron}
          aria-expanded={!collapsed}
          aria-label={`${collapsed ? 'Expand' : 'Collapse'} Field Mapping`}
          onClick={() => onCollapsedChange(!collapsed)}
        >
          <CaretDownIcon size={24} aria-hidden />
        </button>
      </div>
      {collapsed ? null : <div className={styles.body}>{children}</div>}
    </section>
  );
}
